// src/pages/ProfilesPage.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

type ProfileRow = { id: string; name: string; kind: "voters" | "history"; created_at: string };
type Kind = "voters" | "history";

// Required/optional keys for each profile kind
const VOTER_KEYS_REQ = ["voter_id", "address1", "city", "state", "zip"] as const;
const VOTER_KEYS_OPT = [
  "first_name","middle_name","last_name","suffix",
  "address2","county","precinct","dob","registration_status",
  "party","phone","email"
] as const;

const HIST_KEYS_REQ = ["voter_id"] as const;
const HIST_KEYS_OPT = ["election_name","election_date","type"] as const;

export default function ProfilesPage() {
  const campaignId = useCampaignId();

  // list
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // new profile form
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Kind>("voters");
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // load profiles for current campaign
  useEffect(() => {
    (async () => {
      if (!campaignId) return;
      try {
        setErr(null);
        setLoading(true);
        const { data, error } = await supabase
          .from("import_profiles")
          .select("id,name,kind,created_at")
          .eq("campaign_id", campaignId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        setProfiles((data ?? []) as ProfileRow[]);
      } catch (e: any) {
        setErr(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  // Reset mapping when kind changes
  useEffect(() => { setMapping({}); }, [kind]);

  const reqKeys = useMemo(() => (kind === "voters" ? VOTER_KEYS_REQ : HIST_KEYS_REQ), [kind]);
  const optKeys = useMemo(() => (kind === "voters" ? VOTER_KEYS_OPT : HIST_KEYS_OPT), [kind]);

  async function readHeadersFromFile(f: File) {
    setHeaders([]);
    const lower = f.name.toLowerCase();
    try {
      if (lower.endsWith(".csv")) {
        const text = await f.text();
        const firstLine = (text.split(/\r?\n/)?.[0] ?? "").trim();
        const hs = firstLine.split(",").map((h) => h.trim()).filter(Boolean);
        setHeaders(hs);
      } else {
        const XLSXMod = (await import("xlsx")) as any;
        const buf = await f.arrayBuffer();
        const wb = XLSXMod.read(buf, { type: "array" });
        const sh = wb.SheetNames[0];
        const sheet = wb.Sheets[sh];
        const range = XLSXMod.utils.decode_range(sheet["!ref"]);
        const firstRow: string[] = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const addr = XLSXMod.utils.encode_cell({ r: range.s.r, c: C });
          firstRow.push((sheet[addr]?.v ?? "").toString());
        }
        setHeaders(firstRow.filter(Boolean).map((h: string) => h.trim()));
      }
    } catch (e: any) {
      setErr(`Failed to read headers: ${e.message || String(e)}`);
    }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f) await readHeadersFromFile(f);
  }

  function setMapKey(key: string, header: string) {
    setMapping((m) => ({ ...m, [key]: header }));
  }

  async function saveProfile() {
    try {
      setErr(null);
      setMsg(null);
      setSaving(true);

      if (!campaignId) throw new Error("Select a campaign first.");
      if (!name.trim()) throw new Error("Name is required");

      const missing = reqKeys.filter((k) => !mapping[k]);
      if (missing.length) throw new Error(`Map required fields: ${missing.join(", ")}`);

      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error("Not signed in");

      const insert = {
        name: name.trim(),
        kind,
        mapping,
        created_by: user.id,
        campaign_id: campaignId, // scope to current campaign
      };

      const { error } = await supabase.from("import_profiles").insert(insert);
      if (error) throw error;

      // refresh
      const { data } = await supabase
        .from("import_profiles")
        .select("id,name,kind,created_at")
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: false });

      setProfiles((data ?? []) as ProfileRow[]);
      setMsg("Profile saved.");
      setName("");
      setMapping({});
      setFile(null);
      setHeaders([]);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
        Import Mapping Profiles
      </h1>

      {!campaignId && (
        <div style={{ margin: "10px 0" }}>
          Select a campaign from the top menu to manage profiles.
        </div>
      )}

      {err && (
        <div
          style={{
            background: "#ffe8e8",
            border: "1px solid #f2b0b0",
            padding: 8,
            borderRadius: 6,
            margin: "10px 0",
            color: "#a40000",
          }}
        >
          {err}
        </div>
      )}
      {msg && (
        <div
          style={{
            background: "#ecfdf5",
            border: "1px solid #86efac",
            padding: 8,
            borderRadius: 6,
            margin: "10px 0",
            color: "#166534",
          }}
        >
          {msg}
        </div>
      )}

      {/* Create */}
      <section style={{ marginTop: 8, marginBottom: 18 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          Create New Profile
        </h2>

        <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ width: 160 }}>Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hidalgo Voters 2024"
              style={{
                padding: 8,
                border: "1px solid #ddd",
                borderRadius: 8,
                minWidth: 300,
              }}
              disabled={!campaignId}
            />
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ width: 160 }}>Kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as Kind)}
              style={{
                padding: 8,
                border: "1px solid #ddd",
                borderRadius: 8,
                minWidth: 300,
              }}
              disabled={!campaignId}
            >
              <option value="voters">Voters</option>
              <option value="history">History</option>
            </select>
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ width: 160 }}>Sample file (optional)</span>
            <input type="file" accept=".xlsx,.csv" onChange={onPickFile} disabled={!campaignId} />
          </label>

          {file && (
            <div style={{ marginLeft: 160, color: "#555" }}>
              File: <b>{file.name}</b>
            </div>
          )}
          {headers.length > 0 && (
            <div style={{ color: "#555", marginLeft: 160 }}>
              Detected headers: {headers.join(" | ")}
            </div>
          )}

          <div style={{ marginTop: 8, marginLeft: 160 }}>
            <h3 style={{ fontWeight: 600 }}>Required fields</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "200px 1fr",
                gap: 8,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              {reqKeys.map((k) => (
                <MappingRow
                  key={k}
                  label={k}
                  headers={headers}
                  value={mapping[k]}
                  onChange={(h) => setMapKey(k, h)}
                />
              ))}
            </div>

            <h3 style={{ fontWeight: 600, marginTop: 10 }}>Optional fields</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "200px 1fr",
                gap: 8,
                alignItems: "center",
              }}
            >
              {optKeys.map((k) => (
                <MappingRow
                  key={k}
                  label={k}
                  headers={headers}
                  value={mapping[k]}
                  onChange={(h) => setMapKey(k, h)}
                  optional
                />
              ))}
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <button
              onClick={saveProfile}
              disabled={saving || !campaignId}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #0ea5e9",
                background: campaignId && !saving ? "#0ea5e9" : "#9ccff0",
                color: "#fff",
              }}
            >
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>
      </section>

      {/* List */}
      <section>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          My Profiles
        </h2>
        {!campaignId ? (
          <div>Choose a campaign to see profiles.</div>
        ) : loading ? (
          <div>Loading…</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Name</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Kind</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.id}>
                  <td style={{ padding: 8 }}>{p.name}</td>
                  <td style={{ padding: 8 }}>{p.kind}</td>
                  <td style={{ padding: 8 }}>{new Date(p.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {profiles.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: 8, color: "#777" }}>
                    No profiles yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function MappingRow({
  label,
  headers,
  value,
  onChange,
  optional,
}: {
  label: string;
  headers: string[];
  value?: string;
  onChange: (h: string) => void;
  optional?: boolean;
}) {
  return (
    <>
      <div style={{ color: "#374151" }}>
        <code>{label}</code> {!optional && <span style={{ color: "#b91c1c" }}>*</span>}
      </div>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 260 }}
      >
        <option value="">{optional ? "— optional —" : "— choose a column —"}</option>
        {headers.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
    </>
  );
}
